import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { Menu, X } from "lucide-react";

const Header = () => {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { to: "/cammino", label: t("nav_cammino") },
    { to: "/tappe", label: t("nav_tappe") },
    { to: "/territorio", label: t("nav_territorio") },
    { to: "/prodotti", label: t("nav_prodotti") },
    { to: "/servizi", label: t("nav_servizi") },
    { to: "/progetto", label: t("nav_progetto") },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-pietra-light/90 backdrop-blur-md border-b border-carrubo/10 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group min-w-0">
          <img
            src={scrolled ? "/images/ibleo-cammino-unesco-logo.png" : "/images/ibleo-cammino-unesco-logo-white.png"}
            alt="Cammino Ibleo UNESCO"
            data-testid="site-logo"
            className="h-14 lg:h-16 w-auto flex-shrink-0 select-none drop-shadow-[0_2px_8px_rgba(0,0,0,0.25)]"
            draggable="false"
          />
          <div className={`hidden sm:block leading-tight whitespace-nowrap ${scrolled ? "text-grafite" : "text-pietra-light"}`}>
            <div className="text-[0.6rem] tracking-[0.3em] uppercase opacity-80 whitespace-nowrap">Sicilia Orientale</div>
            <div className="text-[0.55rem] tracking-[0.2em] uppercase opacity-55 whitespace-nowrap mt-0.5">UNESCO · Monti Iblei</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  scrolled ? "text-grafite hover:text-carrubo" : "text-pietra-light/90 hover:text-miele"
                } ${isActive ? (scrolled ? "text-carrubo" : "text-miele") : ""}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            aria-label="Switch language"
            className={`text-xs font-bold tracking-[0.2em] uppercase transition-colors ${
              scrolled ? "text-grafite hover:text-carrubo" : "text-pietra-light hover:text-miele"
            }`}
          >
            {lang === "it" ? "EN" : "IT"}
          </button>
          <Link
            to="/diventa-partner"
            data-testid="cta-partner-header"
            className="hidden md:inline-flex items-center gap-2 bg-miele hover:bg-miele-dark text-grafite text-xs font-bold tracking-[0.15em] uppercase px-5 py-2.5 rounded-sm transition-colors"
          >
            {t("nav_partner")}
          </Link>
          <button
            onClick={() => setOpen(!open)}
            data-testid="menu-toggle"
            className={`lg:hidden ${scrolled ? "text-grafite" : "text-pietra-light"}`}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-pietra-light border-t border-carrubo/10 mt-3">
          <nav className="flex flex-col p-6 gap-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.to.replace("/", "")}`}
                className="text-grafite hover:text-carrubo font-medium"
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/diventa-partner"
              onClick={() => setOpen(false)}
              className="mt-2 bg-miele text-grafite text-xs font-bold tracking-[0.15em] uppercase px-5 py-3 rounded-sm text-center"
            >
              {t("nav_partner")}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
